/**
 * Model Viewer Page
 * In-app experience that embeds the core 3D viewer with controls
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Camera, Compass, Eye, Lightbulb, RefreshCw, RotateCcw, Sparkles, Plus } from 'lucide-react';
import { modelsService } from '../services';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CreateEmbedModal from '../components/Embeds/CreateEmbedModal';
import InlineEditableName from '../components/common/InlineEditableName';
import { useToast } from '../hooks/useToast';

const VIEWPOINTS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'left', label: 'Left' },
  { key: 'right', label: 'Right' },
  { key: 'top', label: 'Top' },
  { key: 'bottom', label: 'Bottom' },
] as const;

const LIGHTING_PRESETS = ['neutral', 'runway', 'daylight', 'soft', 'dramatic', 'fabricDetail'] as const;
const ENVIRONMENT_MAPS = ['none', 'studio', 'outdoor', 'sunset', 'neutral'] as const;
const BACKGROUND_TYPES = ['transparent', 'solid', 'gradient', 'envMap'] as const;

// Helper function to convert camelCase to Title Case
const formatPresetName = (preset: string): string => {
  // Handle special cases
  if (preset === 'fabricDetail') return 'Fabric Detail';
  if (preset === 'envMap') return 'Env Map';
  
  // Standard title case: capitalize first letter
  return preset.charAt(0).toUpperCase() + preset.slice(1);
};

const ModelViewer: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [currentLightingPreset, setCurrentLightingPreset] = useState<string>('neutral');
  const [lightingIntensity, setLightingIntensity] = useState(1.0);
  const [cameraFov, setCameraFov] = useState(45);
  const [environmentMap, setEnvironmentMap] = useState<string>('none');
  const [backgroundType, setBackgroundType] = useState<string>('transparent');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [gradientTop, setGradientTop] = useState('#ffffff');
  const [gradientBottom, setGradientBottom] = useState('#999999');
  
  // Modal state
  const [isCreateEmbedModalOpen, setIsCreateEmbedModalOpen] = useState(false);
  const [embedSettings, setEmbedSettings] = useState<any>(null);

  const { data: model, isLoading, error, refetch } = useQuery({
    queryKey: ['model-viewer', id],
    queryFn: () => modelsService.getModel(id || ''),
    enabled: Boolean(id),
  });

  const modelUrl = useMemo(() => {
    if (!model) return null;
    return model.optimisedUrl || model.originalUrl;
  }, [model]);

  const viewerBase = import.meta.env.VITE_VIEWER_BASE_URL || 'http://localhost:4173';
  const viewerOrigin = useMemo(() => {
    try {
      return new URL(viewerBase).origin;
    } catch {
      return window.location.origin;
    }
  }, [viewerBase]);

  // Stable iframe URL — model is loaded via viewer:load postMessage once viewer:ready is received.
  // Including modelUrl in the src would cause the iframe to reload when the API response arrives,
  // creating a race between the reload and the viewer:load postMessage.
  const iframeSrc = useMemo(() => {
    const src = `${viewerBase}/index.html?embed=1`;
    console.log('[ModelViewer] iframe src:', src, '| viewerOrigin:', new URL(viewerBase).origin);
    return src;
  }, [viewerBase]);

  const postToViewer = useCallback(
    (message: any) => {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      target.postMessage(message, viewerOrigin);
    },
    [viewerOrigin]
  );

  // Load settings from localStorage on mount
  useEffect(() => {
    if (!id) return;
    
    const storageKey = `model-viewer-settings-${id}`;
    const savedSettings = localStorage.getItem(storageKey);
    
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        setAutoRotate(settings.autoRotate ?? false);
        setCurrentLightingPreset(settings.lightingPreset ?? 'neutral');
        setLightingIntensity(settings.lightingIntensity ?? 1.0);
        setCameraFov(settings.cameraFov ?? 45);
        setEnvironmentMap(settings.environmentMap ?? 'none');
        setBackgroundType(settings.backgroundType ?? 'transparent');
        setBackgroundColor(settings.backgroundColor ?? '#ffffff');
        setGradientTop(settings.gradientTop ?? '#ffffff');
        setGradientBottom(settings.gradientBottom ?? '#999999');
        console.log('✅ Loaded saved viewer settings for model:', id);
      } catch (error) {
        console.warn('⚠️  Failed to load saved settings:', error);
      }
    }
    
    setSettingsLoaded(true);
  }, [id]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (!id || !settingsLoaded) return;
    
    const storageKey = `model-viewer-settings-${id}`;
    const settings = {
      autoRotate,
      lightingPreset: currentLightingPreset,
      lightingIntensity,
      cameraFov,
      environmentMap,
      backgroundType,
      backgroundColor,
      gradientTop,
      gradientBottom,
    };
    
    localStorage.setItem(storageKey, JSON.stringify(settings));
  }, [
    id,
    settingsLoaded,
    autoRotate,
    currentLightingPreset,
    lightingIntensity,
    cameraFov,
    environmentMap,
    backgroundType,
    backgroundColor,
    gradientTop,
    gradientBottom,
  ]);

  // Unconditional message sniffer — empty dep array, never cleaned up during the session
  useEffect(() => {
    const sniff = (e: MessageEvent) =>
      console.log('[SNIFF] origin:', e.origin, '| data:', JSON.stringify(e.data));
    window.addEventListener('message', sniff);
    return () => window.removeEventListener('message', sniff);
  }, []);

  // Listen for viewer ready handshake
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (!viewerReady) {
        setViewerError('Viewer did not respond. Please retry or check the viewer service.');
      }
    }, 8000);

    const handler = (event: MessageEvent) => {
      console.log('[MessageBus] raw message — origin:', event.origin, '| expected:', viewerOrigin, '| data:', event.data);
      if (event.origin !== viewerOrigin) return;
      if (event.data?.type === 'viewer:ready') {
        setViewerReady(true);
        setViewerError(null);
        iframeRef.current?.contentWindow?.postMessage({ type: 'viewer:ack' }, viewerOrigin);
      }
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      window.clearTimeout(timeout);
    };
  }, [viewerOrigin, viewerReady]);

  // Auto-load model and apply settings when ready
  useEffect(() => {
    if (viewerReady && modelUrl && settingsLoaded) {
      postToViewer({ type: 'viewer:load', payload: { modelUrl, viewpoint: 'front' } });
      
      // Wait a bit for model to load, then apply saved settings
      setTimeout(() => {
        // Apply all saved settings
        postToViewer({ type: 'viewer:lighting', payload: { preset: currentLightingPreset, intensity: lightingIntensity } });
        postToViewer({ type: 'viewer:camera', payload: { fov: cameraFov } });
        postToViewer({ type: 'viewer:environment', payload: { envMapPreset: environmentMap } });
        
        // Apply background based on type
        const payload: any = { background: backgroundType };
        if (backgroundType === 'solid') {
          payload.backgroundColor = backgroundColor;
        } else if (backgroundType === 'gradient') {
          payload.gradientTop = gradientTop;
          payload.gradientBottom = gradientBottom;
        }
        postToViewer({ type: 'viewer:environment', payload });
        
        // Apply auto-rotate
        postToViewer({ type: 'viewer:autoRotate', payload: { enabled: autoRotate } });
        
        console.log('✅ Applied saved settings to viewer');
      }, 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerReady, modelUrl, settingsLoaded]);

  const handleViewpoint = (viewpoint: string) => {
    postToViewer({ type: 'viewer:viewpoint', payload: { viewpoint } });
  };

  const handleLighting = (preset: string) => {
    setCurrentLightingPreset(preset);
    postToViewer({ type: 'viewer:lighting', payload: { preset, intensity: lightingIntensity } });
  };

  const handleLightingIntensity = (intensity: number) => {
    setLightingIntensity(intensity);
    postToViewer({ type: 'viewer:lighting', payload: { intensity } });
  };

  const handleCameraFov = (fov: number) => {
    setCameraFov(fov);
    postToViewer({ type: 'viewer:camera', payload: { fov } });
  };

  const handleEnvironmentMap = (envMapPreset: string) => {
    setEnvironmentMap(envMapPreset);
    postToViewer({ type: 'viewer:environment', payload: { envMapPreset } });
  };

  const handleBackgroundType = (background: string) => {
    setBackgroundType(background);
    const payload: any = { background };
    if (background === 'solid') {
      payload.backgroundColor = backgroundColor;
    } else if (background === 'gradient') {
      payload.gradientTop = gradientTop;
      payload.gradientBottom = gradientBottom;
    }
    postToViewer({ type: 'viewer:environment', payload });
  };

  const handleBackgroundColor = (color: string) => {
    setBackgroundColor(color);
    if (backgroundType === 'solid') {
      postToViewer({ type: 'viewer:environment', payload: { background: 'solid', backgroundColor: color } });
    }
  };

  const handleGradientTop = (color: string) => {
    setGradientTop(color);
    if (backgroundType === 'gradient') {
      postToViewer({ type: 'viewer:environment', payload: { background: 'gradient', gradientTop: color, gradientBottom } });
    }
  };

  const handleGradientBottom = (color: string) => {
    setGradientBottom(color);
    if (backgroundType === 'gradient') {
      postToViewer({ type: 'viewer:environment', payload: { background: 'gradient', gradientTop, gradientBottom: color } });
    }
  };

  const handleAutoRotate = () => {
    const next = !autoRotate;
    setAutoRotate(next);
    postToViewer({ type: 'viewer:autoRotate', payload: { enabled: next } });
  };

  const handleReframe = () => {
    postToViewer({ type: 'viewer:reframe' });
  };

  // Rename the model and refresh the query so the header reflects the new name
  const handleRename = async (newName: string) => {
    if (!id) return;
    await modelsService.renameModel(id, newName);
    await refetch();
    showToast('success', 'Model renamed successfully');
  };

  const handleCreateEmbedWithSettings = () => {
    // Gather all current settings
    const currentSettings = {
      lighting: {
        preset: currentLightingPreset,
        intensity: lightingIntensity
      },
      camera: {
        fov: cameraFov,
        initialViewpoint: 'front',
        autoFrame: true
      },
      environment: {
        background: backgroundType,
        backgroundColor: backgroundType === 'solid' ? backgroundColor : undefined,
        gradientTop: backgroundType === 'gradient' ? gradientTop : undefined,
        gradientBottom: backgroundType === 'gradient' ? gradientBottom : undefined,
        envMapPreset: environmentMap
      },
      controls: {
        enableRotate: true,
        enableZoom: true,
        enablePan: false,
        autoRotate: autoRotate
      }
    };

    setEmbedSettings(currentSettings);
    setIsCreateEmbedModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="space-y-4 rounded-2xl border border-red-200 bg-red-50 p-6">
        <h2 className="text-lg font-semibold text-red-700">Model could not be loaded</h2>
        <p className="text-sm text-red-600">Please try again or return to the models list.</p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => refetch()}>Retry</Button>
          <Button onClick={() => navigate('/models')}>Back to Models</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => navigate('/models')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Models
        </Button>
        <div className="flex items-center gap-2">
          <InlineEditableName
            name={model.name}
            onSave={handleRename}
            className="text-2xl font-bold text-gray-900"
          />
          <span className="text-sm text-gray-500">
            {(model.originalFileSize / (1024 * 1024)).toFixed(2)} MB
          </span>
        </div>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
          {model.status === 'ready' ? 'Ready' : model.status}
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr,320px]">
        {/* Left: 3D Viewer - Fixed size, no overflow */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-black shadow-lg">
          {viewerError && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 text-white">
              <p className="text-sm mb-3">{viewerError}</p>
              <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title="3D Viewer"
            className="h-[70vh] w-full border-0"
            allow="clipboard-write; fullscreen"
          />
        </div>

        {/* Right: Configuration Panel - Scrollable, matches viewer height */}
        <div className="h-[70vh] overflow-y-auto overflow-x-hidden">
          <div className="space-y-4 pr-1">
          {/* Camera Configuration */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Camera Settings</h3>
            </div>
            <div className="mt-3 space-y-3">
              {/* Field of View Slider */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                Field of View: {cameraFov}°
                </label>
                <input
                type="range"
                min="15"
                max="75"
                step="5"
                value={cameraFov}
                onChange={(e) => handleCameraFov(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>Narrow (15°)</span>
                <span>Normal (45°)</span>
                <span>Wide (75°)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Environment Configuration */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-900">Environment</h3>
            </div>
            <div className="mt-3 space-y-3">
              {/* Background Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Background
                </label>
                <select
                  value={backgroundType}
                  onChange={(e) => handleBackgroundType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {BACKGROUND_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Background Color (shown only for solid background) */}
              {backgroundType === 'solid' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Background Colour
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => handleBackgroundColor(e.target.value)}
                      className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={backgroundColor}
                      onChange={(e) => handleBackgroundColor(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
              )}

              {/* Gradient Colors (shown only for gradient background) */}
              {backgroundType === 'gradient' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Gradient Top Colour
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={gradientTop}
                        onChange={(e) => handleGradientTop(e.target.value)}
                        className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={gradientTop}
                        onChange={(e) => handleGradientTop(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Gradient Bottom Colour
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={gradientBottom}
                        onChange={(e) => handleGradientBottom(e.target.value)}
                        className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={gradientBottom}
                        onChange={(e) => handleGradientBottom(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="#999999"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Environment Map */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Environment Map
                </label>
                <select
                  value={environmentMap}
                  onChange={(e) => handleEnvironmentMap(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {ENVIRONMENT_MAPS.map((map) => (
                    <option key={map} value={map}>
                      {map.charAt(0).toUpperCase() + map.slice(1)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-gray-500">
                  HDR map for realistic reflections
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary-600" />
              <h3 className="text-sm font-semibold text-gray-900">Viewpoints</h3>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {VIEWPOINTS.map((vp) => (
                <Button
                  key={vp.key}
                  variant="secondary"
                  size="sm"
                  onClick={() => handleViewpoint(vp.key)}
                  className="w-full justify-center"
                >
                  {vp.label}
                </Button>
              ))}
              <Button variant="secondary" size="sm" onClick={handleReframe} className="col-span-3 justify-center">
                <RotateCcw className="mr-2 h-4 w-4" />
                Reframe
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-gray-900">Lighting</h3>
            </div>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {LIGHTING_PRESETS.map((preset) => (
                  <Button
                    key={preset}
                    variant="secondary"
                    size="sm"
                    onClick={() => handleLighting(preset)}
                    className="w-full justify-center"
                  >
                    {formatPresetName(preset)}
                  </Button>
                ))}
              </div>
              
              {/* Lighting Intensity Slider */}
              <div className="pt-2 border-t border-gray-100">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Intensity: {lightingIntensity.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={lightingIntensity}
                  onChange={(e) => handleLightingIntensity(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                />
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                  <span>Dim</span>
                  <span>Normal</span>
                  <span>Bright</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Compass className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-900">Controls</h3>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={handleAutoRotate} className="justify-center">
                <Eye className="mr-2 h-4 w-4" />
                {autoRotate ? 'Stop Auto-Rotate' : 'Start Auto-Rotate'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => postToViewer({ type: 'viewer:viewpoint', payload: { viewpoint: 'front' } })}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset Camera
              </Button>
            </div>
          </div>

          {/* Quick Actions Section */}
          <div className="rounded-2xl border-2 border-primary-200 bg-primary-50 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-primary-600" />
              <h3 className="text-sm font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <Button 
              onClick={handleCreateEmbedWithSettings}
              className="w-full justify-center"
              variant="primary"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Embed with These Settings
            </Button>
            <p className="text-xs text-gray-600 mt-2 text-center">
              Generate an embed code with your current viewer configuration
            </p>
          </div>
          </div>
        </div>
      </div>

      {/* Create Embed Modal */}
      {isCreateEmbedModalOpen && (
        <CreateEmbedModal
          isOpen={isCreateEmbedModalOpen}
          onClose={() => setIsCreateEmbedModalOpen(false)}
          preSelectedModel={model}
          prefilledSettings={embedSettings}
        />
      )}
    </div>
  );
};

export default ModelViewer;
